/**
 * Reglas de negocio puras para Actas de Cantidades.
 */

import type { Acta, ActaLinea, Rd, So, Tarea, ActaFirma } from "@app-types/entities";
import type { EstadoActa } from "@constants/estados";

const TRANSICIONES_ACTA: Record<EstadoActa, EstadoActa[]> = {
  BORRADOR: ["EN_REVISION_INT", "ANULADA"],
  EN_REVISION_INT: ["APROBADA_INT", "RECHAZADA", "ANULADA"],
  // PL2 puede firmar directo desde APROBADA_INT (workflow simple)
  // o pasar por EN_REVISION_PL2 si quiere marcar "estoy revisando"
  APROBADA_INT: ["APROBADA", "EN_REVISION_PL2", "RECHAZADA", "ANULADA"],
  EN_REVISION_PL2: ["APROBADA", "RECHAZADA", "ANULADA"],
  // El PMIS genera el acta pero NO factura: la facturación es externa. Si el
  // acta ya aprobada se rechaza fuera del sistema, el PL2 la devuelve a
  // BORRADOR para ajustarla y volver a firmarla, o la anula si no debió
  // existir. Volver a BORRADOR conserva las firmas: `firmarPorRol` reemplaza
  // la del mismo rol, así que el circuito se recorre otra vez sin duplicar.
  APROBADA: ["FACTURADA", "BORRADOR", "ANULADA"],
  RECHAZADA: ["BORRADOR", "ANULADA"],
  FACTURADA: ["BORRADOR", "ANULADA"],
  ANULADA: [],
};

/** Estados en los que el acta sigue viva — reclama obra y bloquea. */
export const ESTADOS_ACTA_VIVA: EstadoActa[] = [
  "BORRADOR",
  "EN_REVISION_INT",
  "APROBADA_INT",
  "EN_REVISION_PL2",
  "RECHAZADA",
];

/** Estados en los que el acta cuenta para el acumulado certificado. */
export const ESTADOS_ACTA_CUENTA: EstadoActa[] = ["APROBADA", "FACTURADA"];

export function puedeTransicionarActa(actual: EstadoActa, destino: EstadoActa): boolean {
  return TRANSICIONES_ACTA[actual]?.includes(destino) ?? false;
}

/**
 * ¿Se puede BORRAR físicamente esta acta, o hay que anularla?
 *
 * Solo un borrador sin ninguna firma. En cualquier otro caso el acta ya dejó
 * huella de revisión y el borrado destruiría la única evidencia que existe:
 * `Acta` es la ÚNICA entidad documental del PMIS sin campo `historial`, así
 * que sus firmas viven dentro del propio payload. Y en Supabase el borrado es
 * un DELETE físico sobre `pmis_entities`, sin soft-delete ni papelera.
 *
 * Es la misma doctrina que `puedeEliminarSo` ya escribió para las OT:
 * "cualquier estado posterior implica revisión registrada y debe gestionarse
 * vía CANCELADA".
 */
export function puedeEliminarActa(
  acta: Pick<Acta, "estado" | "firmas">,
): { puede: boolean; motivo?: string } {
  if (acta.estado !== "BORRADOR") {
    return {
      puede: false,
      motivo: "El acta ya salió de borrador. Anúlela en vez de eliminarla, para conservar la evidencia.",
    };
  }
  if ((acta.firmas ?? []).length > 0) {
    return {
      puede: false,
      motivo: "El acta ya tiene firmas registradas. Anúlela en vez de eliminarla.",
    };
  }
  return { puede: true };
}

export function calcularSubtotalLinea(linea: ActaLinea): number {
  return linea.cantidad * linea.precioUnitario;
}

// ── Corte mensual de certificación ──────────────────────────────────────────

/** Día del mes en que cierra el corte de certificación. */
export const DIA_CORTE_CERTIFICACION = 10;
/** Día del mes desde el cual se avisa la proximidad del corte. */
export const DIA_AVISO_CORTE = 5;

/**
 * Fecha de corte (YYYY-MM-DD) del período de certificación del mes de `hoy`:
 * siempre el día 10.
 */
export function fechaCorteCertificacion(
  hoy: string = new Date().toISOString().slice(0, 10),
): string {
  return `${hoy.slice(0, 7)}-${String(DIA_CORTE_CERTIFICACION).padStart(2, "0")}`;
}

/**
 * Aviso de proximidad del corte mensual. Activo entre el día 5 y el día 10
 * de cada mes (inclusive).
 */
export function avisoCorteCertificacion(
  hoy: string = new Date().toISOString().slice(0, 10),
): { activo: boolean; mensaje: string } {
  const dia = Number(hoy.slice(8, 10));
  const activo = dia >= DIA_AVISO_CORTE && dia <= DIA_CORTE_CERTIFICACION;
  return {
    activo,
    mensaje: activo
      ? `Corte de certificación el día ${DIA_CORTE_CERTIFICACION}: prepara y ` +
        `cierra las actas de avance del período antes de esa fecha.`
      : "",
  };
}

/**
 * Costo real acumulado (AC) del proyecto = suma de las certificaciones ya
 * confirmadas (actas APROBADA o FACTURADA). Alimenta el CPI de la Curva S.
 */
export function acumuladoCertificado(
  actas: Pick<Acta, "estado" | "total">[],
): number {
  return actas
    .filter((a) => a.estado === "APROBADA" || a.estado === "FACTURADA")
    .reduce((s, a) => s + (a.total || 0), 0);
}

export function calcularTotalActa(lineas: ActaLinea[]): number {
  return lineas.reduce((sum, l) => sum + calcularSubtotalLinea(l), 0);
}

/**
 * Genera lineas del acta consolidando todas las tarifas de la SO
 * (incluso las que no tuvieron avance) y sumando avances de los RDs.
 *
 * Cada tarifa de la SO genera 1 linea, con la cantidad ejecutada en el periodo
 * (puede ser 0 si no hubo avance).
 */
export function generarLineasActa(
  tareas: Tarea[],
  rdsIntegrados: Rd[],
  periodoInicio: string,
  periodoFin: string,
  /**
   * Regla (d): cantidad ya certificada por tarifa en las actas APROBADAS de la
   * OT. La línea se topa automáticamente al residuo y nunca baja de cero, de
   * modo que la doble certificación es imposible por construcción — aunque los
   * períodos se solapen. Por defecto vacío: las 3 pruebas existentes y los
   * llamadores que no la usan siguen funcionando igual.
   */
  yaCertificado: Record<string, number> = {},
): ActaLinea[] {
  // Map tareaId -> cantidad acumulada en el periodo
  const cantidadesPorTarea: Record<string, number> = {};
  for (const rd of rdsIntegrados) {
    if (rd.fecha < periodoInicio || rd.fecha > periodoFin) continue;
    for (const avance of rd.avancesTareas) {
      // Guarda numerica: un RD historico o migrado puede traer `cantidadDia`
      // ausente o no numerica. Sin esta guarda un solo valor invalido propaga
      // NaN a subtotal, total, totalUsd y totalCop, y deja el acta inservible
      // sin ningun aviso.
      const cant = Number(avance.cantidadDia);
      if (!Number.isFinite(cant)) continue;
      cantidadesPorTarea[avance.tareaId] = (cantidadesPorTarea[avance.tareaId] ?? 0) + cant;
    }
  }

  // 1 linea por TAREA ejecutable, incluso si no tuvo avance. Los renglones
  // GRUPO son cabeceras de consolidacion: nacen con `tarifaId` vacio, nunca
  // reciben avance y solo inflan `lineas.length` y los KPIs del acta.
  // Se compara contra "GRUPO" y NO contra "TAREA" a proposito: `tipo` es
  // opcional y las tareas historicas de produccion vienen sin el campo.
  // Saldo consumible por tarifa. Se descuenta a medida que se recorren las
  // tareas, porque varias tareas pueden compartir la misma tarifa.
  const saldo: Record<string, number> = { ...yaCertificado };

  return tareas
    .filter((t) => t.tipo !== "GRUPO")
    .map((t) => {
      const bruta = cantidadesPorTarea[t.id] ?? 0;
      const cubierto = Math.min(bruta, saldo[t.tarifaId] ?? 0);
      saldo[t.tarifaId] = (saldo[t.tarifaId] ?? 0) - cubierto;
      const cantidad = bruta - cubierto;
      return {
        tarifaId: t.tarifaId,
        descripcion: t.descripcion,
        unidad: t.unidad,
        cantidad,
        precioUnitario: t.precioUnitario,
        subtotal: cantidad * t.precioUnitario,
        // Se conserva lo ejecutado antes del tope para que el documento pueda
        // explicar la diferencia en vez de que parezca un error de cálculo.
        ...(cubierto > 0 && { cantidadBruta: bruta }),
      };
    });
}

/**
 * Codigo del acta. Definicion unica compartida por los tres adaptadores
 * espejo, que antes repetian el mismo template literal.
 *
 * Antes era `ACTA-{OT}-{YYYY-MM del inicio}`: dos actas del mismo mes para la
 * misma OT producian el MISMO codigo, y ese codigo es el `entidadId` con el
 * que la bitacora de auditoria referencia el acta, de modo que sus entradas
 * quedaban mezcladas e indistinguibles. Anclarlo a la fecha de corte completa
 * elimina la colision salvo que se certifiquen dos veces exactamente las
 * mismas fechas de fin.
 */
export function codigoActa(codigoSo: string, periodoFin: string): string {
  return `ACTA-${codigoSo}-${periodoFin}`;
}

/** Filtra solo las lineas con cantidad > 0 (para reportes ejecutivos). */
export function lineasConAvance(lineas: ActaLinea[]): ActaLinea[] {
  return lineas.filter((l) => l.cantidad > 0);
}

/** Verifica si todas las firmas requeridas estan dadas. */
export function tieneFirmasCompletas(firmas: ActaFirma[]): boolean {
  const rolesFirmaron = new Set(firmas.filter((f) => f.decision === "APROBADA").map((f) => f.rol));
  // Requeridas (CONT eliminado del PMIS):
  //   - Lado contratista: ADOR (titular) o PLANNER (back).
  //   - Interventoría: INT.
  //   - Tecpetrol: PL2.
  const firmaContratista = rolesFirmaron.has("ADOR") || rolesFirmaron.has("PLANNER");
  return firmaContratista && rolesFirmaron.has("INT") && rolesFirmaron.has("PL2");
}

export function tieneRechazos(firmas: ActaFirma[]): boolean {
  return firmas.some((f) => f.decision === "RECHAZADA");
}

export function validarActa(acta: Acta): { valido: boolean; errores: string[] } {
  const errores: string[] = [];
  if (!acta.soId) errores.push("Orden de Trabajo es obligatoria");
  if (!acta.periodoInicio || !acta.periodoFin) errores.push("Periodo es obligatorio");
  if (acta.periodoInicio > acta.periodoFin) errores.push("Inicio del periodo debe ser anterior al fin");
  if (acta.lineas.length === 0) errores.push("El acta debe tener al menos una linea");
  return { valido: errores.length === 0, errores };
}

/** KPIs para mostrar en encabezado de actas. */
export function calcularKpisActa(acta: Acta) {
  return {
    totalLineas: acta.lineas.length,
    lineasConAvance: lineasConAvance(acta.lineas).length,
    total: acta.total,
    tieneTodasFirmas: tieneFirmasCompletas(acta.firmas),
    tieneRechazos: tieneRechazos(acta.firmas),
  };
}

// ── Calificación del contratista en el periodo del acta ─────────────────────
//
// El Interventor propone una calificación 1-5 ⭐ por RD (`Rd.calificacionInt`)
// y el PL/PL2 la valida al integrar el reporte. La calificación del acta es
// la media de las calificaciones de los RDs cuyo `fecha` cae dentro del
// período del acta y que están ya integrados (APROBADO) o esperando a PL2
// (EN_REVISION + nivelRevision=PL).
//
// Esa misma fuente es la que alimenta el promedio histórico del contratista
// en el módulo Contratos Marco (`calificacionMediaPorContrato`), de modo que
// la nota del acta es un "zoom" del período sobre la métrica global.

export interface CalificacionActa {
  /** Media aritmética de las calificaciones (1-5). `null` si no hay evaluaciones. */
  media: number | null;
  /** Número de RDs con calificación contabilizados en el periodo. */
  nEvaluaciones: number;
}

/** Límite duro del campo de notas del acta. */
export const NOTAS_ACTA_MAX_CHARS = 300;

export function calificacionMediaActa(
  rds: Pick<Rd, "fecha" | "estado" | "nivelRevision" | "calificacionInt">[],
  periodoInicio: string,
  periodoFin: string,
): CalificacionActa {
  const enPeriodo = rds
    .filter((r) => r.fecha >= periodoInicio && r.fecha <= periodoFin)
    .filter((r) => typeof r.calificacionInt === "number")
    .filter(
      (r) =>
        r.estado === "APROBADO" ||
        (r.estado === "EN_REVISION" && r.nivelRevision === "PL"),
    )
    .map((r) => r.calificacionInt as number);
  if (enPeriodo.length === 0) return { media: null, nEvaluaciones: 0 };
  const suma = enPeriodo.reduce((a, b) => a + b, 0);
  return { media: suma / enPeriodo.length, nEvaluaciones: enPeriodo.length };
}

// ============================================================================
// Ventana del corte de certificación — reglas (b) a (e) del responsable
// ============================================================================

/**
 * Suma un día a una fecha "YYYY-MM-DD", en UTC. La aritmética de la ventana
 * tiene que ser estable entre usuarios y no depender del reloj del equipo.
 */
export function diaSiguiente(fecha: string): string {
  const d = new Date(fecha + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/** Fechas DISTINTAS con reporte diario APROBADO, ascendentes. */
export function fechasRdAprobado(rds: Pick<Rd, "fecha" | "estado">[]): string[] {
  const set = new Set<string>();
  for (const r of rds) {
    if (r.estado === "APROBADO" && r.fecha) set.add(r.fecha);
  }
  return [...set].sort();
}

/** Última fecha con reporte APROBADO. `null` = esta OT no puede certificar. */
export function ultimoRdAprobado(rds: Pick<Rd, "fecha" | "estado">[]): string | null {
  const fechas = fechasRdAprobado(rds);
  return fechas.length ? (fechas[fechas.length - 1] as string) : null;
}

/**
 * Inicio del PRIMER corte — regla (b): "desde el día de inicio de la OT".
 *
 * Se toma el MÍNIMO entre la fecha contractual y el primer reporte aprobado, y
 * no solo la contractual: `periodoInicio` actúa como cota inferior del filtro,
 * así que si quedara por encima del primer reporte esa obra no entraría nunca
 * — y bajo la regla (c) los cortes siguientes ya empiezan después.
 *
 * NO se usa `fechaInicioEjecucion`: pese al nombre, los tres adaptadores la
 * escriben con la fecha del día en que la OT pasa a APROBADA. Es la fecha de
 * aprobación de la cotización, no la de inicio de obra.
 */
export function inicioPrimerCorte(
  so: Pick<So, "fechaInicio" | "fechaInicioReal">,
  rds: Pick<Rd, "fecha" | "estado">[],
): string {
  const candidatos = [
    so.fechaInicioReal ?? "",
    so.fechaInicio ?? "",
    fechasRdAprobado(rds)[0] ?? "",
  ].filter((f) => f !== "");
  return candidatos.sort()[0] ?? "";
}

/**
 * Frontera de lo ya certificado — regla (c): "a partir de la última
 * facturación, sin repisar lo ya facturado".
 *
 * Se apoya en `ultimoRdIncluido`, que cada acta persiste al generarse. Ese
 * campo es lo que evita la SUB-CERTIFICACIÓN silenciosa: derivar la frontera
 * del calendario de los reportes presupone que el acta certificó todo lo
 * aprobado hasta su `periodoFin`, y eso es falso para los reportes que se
 * aprobaron DESPUÉS de generarla — obra que quedaría huérfana para siempre y
 * sin ninguna señal en pantalla. Para las actas anteriores a este cambio se
 * cae a `periodoFin`.
 */
export function ultimoCorteCertificado(
  actasDeLaOt: Pick<Acta, "estado" | "periodoFin" | "ultimoRdIncluido">[],
): string | null {
  const cortes = actasDeLaOt
    .filter((a) => ESTADOS_ACTA_CUENTA.includes(a.estado))
    .map((a) => a.ultimoRdIncluido ?? a.periodoFin)
    .filter((f): f is string => !!f)
    .sort();
  return cortes.length ? (cortes[cortes.length - 1] as string) : null;
}

/**
 * Regla (e): una sola acta viva por OT. Devuelve la que bloquea, o `null`.
 *
 * Como solo las aprobadas restan por cantidad, dos actas en revisión
 * simultánea podrían reclamar la misma obra y ambas llegar a APROBADA. Con una
 * sola viva eso es imposible. RECHAZADA cuenta como viva: no es terminal,
 * vuelve a BORRADOR — sigue en el circuito.
 *
 * La salida cuando una queda abandonada es anularla o eliminarla; por eso esa
 * capacidad es prerrequisito de esta regla y no una mejora aparte.
 */
export function actaVivaDeSo<T extends Pick<Acta, "estado">>(actasDeLaOt: T[]): T | null {
  return actasDeLaOt.find((a) => ESTADOS_ACTA_VIVA.includes(a.estado)) ?? null;
}

/**
 * Regla (d): cantidad ya certificada por tarifa en las actas que cuentan.
 * Es el sustraendo del tope automático. La llave es `tarifaId`, que es lo que
 * `ActaLinea` persiste.
 */
export function cantidadesYaCertificadas(
  actasDeLaOt: Pick<Acta, "estado" | "lineas">[],
): Record<string, number> {
  const acc: Record<string, number> = {};
  for (const a of actasDeLaOt) {
    if (!ESTADOS_ACTA_CUENTA.includes(a.estado)) continue;
    for (const l of a.lineas ?? []) {
      if (!l.tarifaId) continue;
      const q = Number(l.cantidad);
      acc[l.tarifaId] = (acc[l.tarifaId] ?? 0) + (Number.isFinite(q) ? q : 0);
    }
  }
  return acc;
}

export type MotivoSinVentana =
  | "SIN_RD_APROBADOS"
  | "SIN_OBRA_NUEVA"
  | "ACTA_VIVA"
  | "CORTE_FUERA_DE_RANGO"
  | "CORTE_SIN_RD_APROBADO";

export type VentanaCorte =
  | {
      ok: true;
      inicio: string;
      fin: string;
      finSugerido: string;
      esPrimerCorte: boolean;
      fechasElegibles: string[];
    }
  | { ok: false; motivo: MotivoSinVentana; mensaje: string };

/**
 * Resuelve las reglas (b), (c) y (e) en una sola función, compartida por la
 * interfaz —que la usa para el preview y para deshabilitar el botón— y por los
 * tres adaptadores, que traducen el fallo a `ValidationError`.
 *
 * Unión discriminada y no excepción, justamente para que sirva a las dos.
 */
export function ventanaCorteCertificacion(
  so: Pick<So, "fechaInicio" | "fechaInicioReal">,
  rdsDeLaOt: Pick<Rd, "fecha" | "estado">[],
  actasDeLaOt: Pick<Acta, "estado" | "periodoFin" | "ultimoRdIncluido">[],
  fechaCorteElegida: string | null,
): VentanaCorte {
  if (actaVivaDeSo(actasDeLaOt)) {
    return {
      ok: false,
      motivo: "ACTA_VIVA",
      mensaje:
        "Esta OT ya tiene un acta en curso. Ciérrela, anúlela o elimínela antes de generar otra.",
    };
  }

  const fechas = fechasRdAprobado(rdsDeLaOt);
  const finMaximo = fechas.length ? (fechas[fechas.length - 1] as string) : null;
  if (!finMaximo) {
    return {
      ok: false,
      motivo: "SIN_RD_APROBADOS",
      mensaje:
        "Esta OT no tiene ningún reporte diario aprobado, así que no hay obra que certificar.",
    };
  }

  const ultimoCorte = ultimoCorteCertificado(actasDeLaOt);
  const esPrimerCorte = ultimoCorte === null;
  const inicio = esPrimerCorte ? inicioPrimerCorte(so, rdsDeLaOt) : diaSiguiente(ultimoCorte);

  if (inicio > finMaximo) {
    return {
      ok: false,
      motivo: "SIN_OBRA_NUEVA",
      mensaje:
        "No hay obra nueva desde el último corte: todos los reportes aprobados ya están certificados.",
    };
  }

  const elegibles = fechas.filter((f) => f >= inicio);

  if (fechaCorteElegida === null) {
    return {
      ok: true,
      inicio,
      fin: finMaximo,
      finSugerido: finMaximo,
      esPrimerCorte,
      fechasElegibles: elegibles,
    };
  }
  if (fechaCorteElegida < inicio || fechaCorteElegida > finMaximo) {
    return {
      ok: false,
      motivo: "CORTE_FUERA_DE_RANGO",
      mensaje: "La fecha de corte debe estar entre " + inicio + " y " + finMaximo + ".",
    };
  }
  if (!elegibles.includes(fechaCorteElegida)) {
    return {
      ok: false,
      motivo: "CORTE_SIN_RD_APROBADO",
      mensaje:
        "La fecha de corte debe coincidir con un día que tenga reporte diario aprobado, " +
        "para que el acta sea verificable contra los reportes.",
    };
  }
  return {
    ok: true,
    inicio,
    fin: fechaCorteElegida,
    finSugerido: finMaximo,
    esPrimerCorte,
    fechasElegibles: elegibles,
  };
}

/**
 * Todo el cálculo del borrador de acta, en un solo sitio y sin acceso a datos.
 *
 * Los tres adaptadores son espejo por diseño, pero solo deben diferir en CÓMO
 * leen; el QUÉ calculan tiene que ser idéntico. Antes cada uno repetía el
 * filtro, la llamada a `generarLineasActa` y el template del código, y era
 * donde se colaban las divergencias.
 */
export type BorradorActa =
  | { ok: false; mensaje: string }
  | {
      ok: true;
      periodoInicio: string;
      periodoFin: string;
      lineas: ActaLinea[];
      total: number;
      /** Frontera real de lo certificado — ver `Acta.ultimoRdIncluido`. */
      ultimoRdIncluido: string;
    };

export function construirBorradorActa(
  so: Pick<So, "fechaInicio" | "fechaInicioReal"> | null | undefined,
  tareas: Tarea[],
  rdsDeLaOt: Rd[],
  actasDeLaOt: Acta[],
  fechaCorte: string | null,
): BorradorActa {
  if (!so) {
    return { ok: false, mensaje: "No se encontró la orden de trabajo del acta." };
  }
  const ventana = ventanaCorteCertificacion(so, rdsDeLaOt, actasDeLaOt, fechaCorte);
  if (!ventana.ok) return { ok: false, mensaje: ventana.mensaje };

  const rdsIntegrados = rdsDeLaOt.filter((r) => r.estado === "APROBADO");
  const lineas = generarLineasActa(
    tareas,
    rdsIntegrados,
    ventana.inicio,
    ventana.fin,
    cantidadesYaCertificadas(actasDeLaOt),
  );

  // El último reporte APROBADO que esta acta llegó a consolidar. No es
  // `ventana.fin` sin más: si el corte se fijó en un día sin reporte —o si el
  // último reporte del rango es anterior— la frontera debe quedar en el
  // reporte real, no en la fecha declarada.
  const incluidas = fechasRdAprobado(rdsDeLaOt).filter(
    (f) => f >= ventana.inicio && f <= ventana.fin,
  );
  const ultimoRdIncluido = incluidas.length
    ? (incluidas[incluidas.length - 1] as string)
    : ventana.fin;

  return {
    ok: true,
    periodoInicio: ventana.inicio,
    periodoFin: ventana.fin,
    lineas,
    total: calcularTotalActa(lineas),
    ultimoRdIncluido,
  };
}
